import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { PageHeader } from "@/components/app-layout";
import { LoadMore } from "@/components/load-more";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { AccountSelector } from "@/components/account-selector";
import { CampaignCard } from "@/components/campaign-card";
import { CampaignEditor } from "@/components/campaign-editor";
import { useAccounts } from "@/lib/accounts";
import { useCampaigns, type Campaign } from "@/lib/campaigns";

export function CampaignsPage() {
    const { data: accounts } = useAccounts();
    const [accountId, setAccountId] = useState<string>();
    const [editor, setEditor] = useState<{ open: boolean; editing: Campaign | null }>({ open: false, editing: null });

    useEffect(() => {
        const first = accounts?.[0];
        if (!accountId && first) setAccountId(first.id);
    }, [accounts, accountId]);

    const campaignQuery = useCampaigns(accountId);
    const campaigns = campaignQuery.items;

    if (!accounts?.length) {
        return (
            <>
                <PageHeader title="Chiến dịch" description="Soạn nội dung, chọn nhóm gửi, gửi ngay hoặc hẹn lịch" />
                <div className="p-8">
                    <Alert tone="info">Chưa có tài khoản Zalo nào. Thêm tài khoản trước rồi quay lại đây.</Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Chiến dịch"
                description="Soạn nội dung, chọn nhóm gửi, gửi ngay hoặc hẹn lịch"
                action={
                    <Button onClick={() => setEditor({ open: true, editing: null })}>
                        <Plus className="size-4" />
                        Chiến dịch mới
                    </Button>
                }
            />

            <div className="space-y-4 p-8">
                <AccountSelector accounts={accounts} value={accountId} onChange={setAccountId} />

                {campaigns?.length === 0 && (
                    <Card>
                        <CardBody className="py-14 text-center text-sm text-ink-muted">
                            Chưa có chiến dịch nào.
                        </CardBody>
                    </Card>
                )}

                {campaigns.map((campaign: Campaign) => (
                    <CampaignCard
                        key={campaign.id}
                        campaign={campaign}
                        onEdit={() => setEditor({ open: true, editing: campaign })}
                    />
                ))}

                <LoadMore
                    shown={campaigns.length}
                    total={campaignQuery.total}
                    hasMore={campaignQuery.hasMore}
                    loading={campaignQuery.isLoadingMore}
                    onLoadMore={campaignQuery.loadMore}
                    unit="chiến dịch"
                />
            </div>

            {editor.open && accountId && (
                <CampaignEditor
                    accountId={accountId}
                    editing={editor.editing}
                    onClose={() => setEditor({ open: false, editing: null })}
                />
            )}
        </>
    );
}
